import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import FreezingRequestServices from "../../UserServices/FreezingRequestServices";
import logo from "../../icons/logo.png";
import "./FreezingManagement.css";

const TODAY = () => new Date().toLocaleDateString("en-CA");

function statusBadge(status) {
  const map = {
    pending: "warning",
    approved: "success",
    declined: "danger",
    cancelled: "secondary",
  };
  return map[status] || "secondary";
}

function fmtDate(v) {
  if (!v) return "—";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function FreezingManagement() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const canManage = role === "1" || role === "3";

  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branch_id: branch_id || "",
    status: "pending",
    search: "",
    from_date: "",
    to_date: "",
  });
  const [applied, setApplied] = useState(filters);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [notesDraft, setNotesDraft] = useState({});
  const [showQr, setShowQr] = useState(false);
  const printRef = useRef(null);

  const publicFormUrl = useMemo(() => {
    const origin = window.location.origin.replace(/\/$/, "");
    return `${origin}/freezing-request`;
  }, []);

  useEffect(() => {
    UserServices.branchName()
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setBranches(Array.isArray(list) ? list : []);
      })
      .catch(() => setBranches([]));
  }, []);

  const load = useCallback(
    async (page = 1, f = applied) => {
      setLoading(true);
      try {
        const res = await FreezingRequestServices.list({
          page,
          per_page: 25,
          branch_id: f.branch_id || undefined,
          status: f.status || undefined,
          search: f.search || undefined,
          from_date: f.from_date || undefined,
          to_date: f.to_date || undefined,
        });
        const pageData = res.data?.data;
        setRows(pageData?.data || []);
        setMeta({
          current_page: pageData?.current_page || 1,
          last_page: pageData?.last_page || 1,
          total: pageData?.total || 0,
        });
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load requests");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [applied]
  );

  useEffect(() => {
    load(1, applied);
  }, [applied, load]);

  const applyFilters = () => setApplied({ ...filters });

  const act = async (id, action) => {
    const notes = (notesDraft[id] || "").trim();
    try {
      if (action === "approve") {
        await FreezingRequestServices.approve(id, notes || undefined);
        toast.success("Approved — freeze applied");
      } else {
        await FreezingRequestServices.decline(id, notes || undefined);
        toast.success("Declined");
      }
      load(meta.current_page, applied);
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed");
    }
  };

  const printQr = () => {
    const node = printRef.current;
    if (!node) return;
    const win = window.open("", "_blank", "width=800,height=900");
    if (!win) {
      toast.error("Allow pop-ups to print the QR poster");
      return;
    }
    win.document.write(`<!DOCTYPE html><html><head><title>Freezing Request QR</title>
      <style>
        @page { size: A4; margin: 18mm; }
        body { font-family: Georgia, 'Times New Roman', serif; text-align: center; color: #1a1a1a; }
        .logo { height: 72px; margin-bottom: 12px; }
        h1 { font-size: 28px; letter-spacing: 0.04em; margin: 8px 0 4px; color: #8b0000; }
        .tag { font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: #666; margin-bottom: 18px; }
        .desc { max-width: 420px; margin: 0 auto 22px; font-size: 14px; line-height: 1.45; color: #333; }
        .qr-wrap { display: inline-block; padding: 18px; border: 2px solid #8b0000; }
        .url { margin-top: 16px; font-size: 11px; word-break: break-all; color: #555; }
        .foot { margin-top: 28px; font-size: 12px; color: #777; }
      </style></head><body>${node.innerHTML}</body></html>`);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 350);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />

          <div className="card freezing-mgmt-card">
            <div className="card-header py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0 font-20">Freezing Management</h5>
                <p className="small text-muted mb-0 mt-1">
                  {canManage
                    ? "Review public QR requests. Approve applies the freeze and extends package expiry."
                    : "View request status and admin notes. Approval is handled by admin."}
                </p>
              </div>
              {canManage ? (
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => setShowQr(true)}
                >
                  <i className="bi bi-qr-code me-1" />
                  Generate QR poster
                </button>
              ) : null}
            </div>

            <div className="card-body">
              <div className="fm-filters row g-3 align-items-end mb-3">
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">Branch</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.branch_id}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, branch_id: e.target.value }))
                    }
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, status: e.target.value }))
                    }
                  >
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.from_date}
                    max={TODAY()}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, from_date: e.target.value }))
                    }
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.to_date}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, to_date: e.target.value }))
                    }
                  />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label small mb-1">Search</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Name / Member ID / email"
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, search: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyFilters();
                    }}
                  />
                </div>
                <div className="col-12 col-md-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-danger w-100"
                    onClick={applyFilters}
                  >
                    Go
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-striped table-hover align-middle mb-0 fm-table">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}>#</th>
                      <th>Member</th>
                      <th>Branch</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th>Admin notes</th>
                      <th>Submitted</th>
                      {canManage ? <th style={{ width: 110 }}>Actions</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={canManage ? 8 : 7} className="text-center py-4">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={canManage ? 8 : 7}
                          className="text-center text-muted py-5"
                        >
                          No freezing requests found for these filters.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>
                            <div className="fw-semibold text-capitalize">
                              {r.client_name}
                            </div>
                            <div className="small text-muted">{r.membership_uid}</div>
                            <div className="small text-muted">{r.email}</div>
                          </td>
                          <td>{r.branch?.name || "—"}</td>
                          <td className="small">
                            {fmtDate(r.requested_start_date)} →{" "}
                            {fmtDate(r.requested_end_date)}
                            <div className="text-muted">{r.duration_days} days</div>
                            {r.reason ? (
                              <div className="text-muted fst-italic">{r.reason}</div>
                            ) : null}
                          </td>
                          <td>
                            <span
                              className={`badge text-uppercase bg-${statusBadge(
                                r.status
                              )}`}
                            >
                              {r.status}
                            </span>
                            {r.reviewer ? (
                              <div className="small text-muted mt-1">
                                by {r.reviewer.first_name} {r.reviewer.last_name}
                              </div>
                            ) : null}
                          </td>
                          <td className="small fm-notes-cell">
                            {r.status === "pending" && canManage ? (
                              <textarea
                                className="form-control form-control-sm"
                                rows={2}
                                placeholder="Optional notes"
                                value={notesDraft[r.id] || ""}
                                onChange={(e) =>
                                  setNotesDraft((d) => ({
                                    ...d,
                                    [r.id]: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              r.admin_notes || "—"
                            )}
                          </td>
                          <td className="small text-nowrap">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleString()
                              : "—"}
                          </td>
                          {canManage ? (
                            <td>
                              {r.status === "pending" ? (
                                <div className="d-grid gap-1">
                                  <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={() => act(r.id, "approve")}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => act(r.id, "decline")}
                                  >
                                    Decline
                                  </button>
                                </div>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <span className="small text-muted">
                  {meta.total} request{meta.total === 1 ? "" : "s"}
                </span>
                {meta.last_page > 1 ? (
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={meta.current_page <= 1}
                      onClick={() => load(meta.current_page - 1, applied)}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled
                    >
                      {meta.current_page} / {meta.last_page}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={meta.current_page >= meta.last_page}
                      onClick={() => load(meta.current_page + 1, applied)}
                    >
                      Next
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </main>

      {showQr && canManage ? (
        <div className="fm-qr-modal" role="dialog" aria-modal="true">
          <div className="fm-qr-dialog">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Print-ready QR</h6>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowQr(false)}
              />
            </div>
            <div ref={printRef} className="fm-qr-print">
              <img src={logo} alt="Vostro World" className="logo" />
              <div className="tag">Health | Fitness | Wellness</div>
              <h1>Freezing Request</h1>
              <p className="desc">
                Scan this QR code with your phone camera to open the membership
                freeze request form. Have your Vostro membership card ready — you
                will need your name, Member ID, registered email, and outlet.
              </p>
              <div className="qr-wrap">
                <QRCodeSVG
                  value={publicFormUrl}
                  size={220}
                  level="M"
                  includeMargin
                />
              </div>
              <div className="url">{publicFormUrl}</div>
              <div className="foot">Vostro World — reception counter</div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="button" className="btn btn-danger" onClick={printQr}>
                Print / Save PDF
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowQr(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
